import { useCallback, useEffect, useRef, useState } from "react";
import useBodyScrollLock from "hooks/useBodyScrollLock";

/**
 * 첫 방문 튜토리얼 오버레이 (공통) — 어두운 반투명 배경 + 실제 요소 스팟라이트 + 순차 스와이프.
 *  - steps: [{ target: 'CSS 셀렉터', text: '안내 문구' }] — 스와이프/닷으로 한 스텝씩 강조(A안)
 *  - 요소 위치는 getBoundingClientRect 실시간 계산(+resize/scroll 추적) → 모든 기기 폭 자동 대응
 *  - 다중 화면 재사용: steps 배열만 갈아끼우면 됨. 새 페이지 CSS 불개입(전역 fixed 레이어).
 */
const BRAND = "#F58232";
const PAD = 6;          // 스팟라이트 여백
const RADIUS = 14;      // 스팟라이트 라운드

// onTargetClick(선택): 비춘 요소 자리를 누르면 안내를 넘기는 대신 이 동작을 한다(그 요소를 직접 누른 것처럼).
export default function TutorialOverlay({ steps, open, onClose, onTargetClick }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const touchX = useRef(null);
  const rafRef = useRef(0);
  useBodyScrollLock(!!open);

  const step = steps[idx];

  // 대상 요소 rect 계산 — 화면 밖이면 가운데로 스크롤 후 추적
  const measure = useCallback(() => {
    if (!open || !step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
  }, [open, step]);

  // 스텝 진입: 대상이 뷰포트 밖이면 스크롤 → 이후 스크롤/리사이즈 동안 실시간 추적
  useEffect(() => {
    if (!open || !step) return;
    setIdx((i) => i); // noop guard
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      const out = r.top < 60 || r.bottom > window.innerHeight - 120;
      if (out) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const track = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", track);
    window.addEventListener("scroll", track, true);
    const t = setInterval(track, 250); // smooth scroll 중 보정
    return () => {
      window.removeEventListener("resize", track);
      window.removeEventListener("scroll", track, true);
      clearInterval(t);
      cancelAnimationFrame(rafRef.current);
    };
  }, [open, step, measure]);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  if (!open || !step) return null;

  const next = () => (idx < steps.length - 1 ? setIdx(idx + 1) : onClose());
  const prev = () => idx > 0 && setIdx(idx - 1);

  // 말풍선 위치 — 요소 아래 공간이 부족하면 위로
  const vh = window.innerHeight;
  const below = rect ? rect.top + rect.height + 140 < vh : true;
  const bubbleTop = rect ? (below ? rect.top + rect.height + 18 : undefined) : vh * 0.4;
  const bubbleBottom = rect && !below ? vh - rect.top + 18 : undefined;

  return (
    <div
      role="dialog" aria-label="튜토리얼"
      style={{ position: "fixed", inset: 0, zIndex: 9999, touchAction: "pan-y" }}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (dx < -40) next();
        else if (dx > 40) prev();
      }}
      onClick={(e) => {
        if (onTargetClick && rect && e.clientX >= rect.left && e.clientX <= rect.left + rect.width
          && e.clientY >= rect.top && e.clientY <= rect.top + rect.height) { onTargetClick(); return; }
        next();
      }}
    >
      {/* 어두운 배경 + 스팟라이트 구멍(SVG 마스크 — 구멍 여러 개도 가능) */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="tut-mask">
            <rect width="100%" height="100%" fill="#fff" />
            {rect && (
              <rect x={rect.left} y={rect.top} width={rect.width} height={rect.height}
                rx={RADIUS} fill="#000" />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.62)" mask="url(#tut-mask)" />
      </svg>

      {/* 스팟라이트 테두리 */}
      {rect && (
        <div style={{
          position: "absolute", top: rect.top, left: rect.left, width: rect.width, height: rect.height,
          border: `2px solid ${BRAND}`, borderRadius: RADIUS, boxSizing: "border-box",
          transition: "all .25s ease", pointerEvents: "none",
        }} />
      )}

      {/* 점선 연결선 */}
      {rect && (
        <div style={{
          position: "absolute", left: rect.left + rect.width / 2,
          top: below ? rect.top + rect.height + 2 : undefined,
          bottom: !below ? vh - rect.top + 2 : undefined,
          height: 14, borderLeft: `2px dashed ${BRAND}`, pointerEvents: "none",
        }} />
      )}

      {/* 번호 배지 + 말풍선 */}
      <div style={{
        position: "absolute", left: "50%", transform: "translateX(-50%)",
        top: bubbleTop, bottom: bubbleBottom, width: "min(30rem, 84vw)",
        background: "rgba(255,255,255,0.97)", borderRadius: "1.4rem",
        padding: "1.4rem 1.6rem", boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
        transition: "all .25s ease",
      }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
          {steps.length > 1 && <span style={{
            flex: "0 0 auto", width: "2.4rem", height: "2.4rem", borderRadius: "50%",
            background: BRAND, color: "#fff", fontWeight: 700, fontSize: "1.4rem",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{idx + 1}</span>}
          <p style={{ fontSize: "1.5rem", lineHeight: 1.5, color: "#222", margin: 0, fontWeight: 500 }}>
            {step.text}
          </p>
        </div>
      </div>

      {/* 건너뛰기 */}
      <button type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: "absolute", top: "max(1.2rem, env(safe-area-inset-top))", right: "1.6rem",
          background: "transparent", border: "none", color: "#fff",
          fontSize: "1.6rem", fontWeight: 600, padding: "0.8rem",
        }}>건너뛰기</button>

      {/* 하단 컨트롤 — [이전] 닷 [다음/완료] (스와이프와 병행).
          한 단계짜리(하단 탭 안내 등)는 숨긴다 — 아래 탭을 가리고, 아무 곳이나 누르면 닫힌다. */}
      {steps.length > 1 && <div style={{
        position: "absolute", left: 0, right: 0,
        bottom: "max(2rem, env(safe-area-inset-bottom))",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 2rem",
      }}>
        <button type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          disabled={idx === 0}
          style={{
            minWidth: "8rem", padding: "1rem 1.4rem", borderRadius: "1.2rem",
            border: "1px solid rgba(255,255,255,0.6)", background: "transparent",
            color: "#fff", fontSize: "1.4rem", fontWeight: 600,
            opacity: idx === 0 ? 0.35 : 1,
          }}>이전</button>
        <div style={{ display: "flex", gap: "0.8rem" }}>
          {steps.map((_, i) => (
            <span key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} style={{
              width: "0.9rem", height: "0.9rem", borderRadius: "50%",
              background: i === idx ? BRAND : "rgba(255,255,255,0.5)",
              transition: "background .2s",
            }} />
          ))}
        </div>
        <button type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          style={{
            minWidth: "8rem", padding: "1rem 1.4rem", borderRadius: "1.2rem",
            border: "none", background: BRAND, color: "#fff",
            fontSize: "1.4rem", fontWeight: 700,
          }}>{idx === steps.length - 1 ? "완료" : "다음"}</button>
      </div>}
    </div>
  );
}
